// Date helpers that avoid the `new Date("YYYY-MM-DD")` UTC-parse pitfall.
//
// JS parses a bare date-only string ("2026-10-07") as UTC midnight, which then
// renders as the *previous* day in negative-offset (US) timezones — e.g. an
// event stored as Oct 7 shows as Oct 6. These helpers parse date-only strings
// at LOCAL midnight. Full ISO timestamps (containing a 'T') are real instants
// and are parsed normally.

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse a date string to a Date. Date-only strings are parsed at LOCAL midnight. */
export function parseDateOnly(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = DATE_ONLY.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a date-only (or ISO) string for display without timezone shift. */
export function formatEventDate(
  value: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string | null {
  const d = parseDateOnly(value);
  return d ? d.toLocaleDateString('en-US', opts) : null;
}

/** Local "today" as YYYY-MM-DD (not UTC — avoids off-by-one near midnight). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Anchor a calendar day at noon UTC for writing into a Salesforce `datetime`
 * field.
 *
 * Salesforce types `Last_Follow_Up_Date__c` as `datetime`. Sending a bare
 * `YYYY-MM-DD` makes Salesforce store midnight UTC, which every
 * negative-offset (US) viewer then reads as the PREVIOUS day. Anchoring at
 * 12:00Z keeps the intended calendar day intact from UTC-11 to UTC+11.
 *
 * Contract: date-only in, anchored instant out; null/empty in, null out. A
 * value that is already a full instant is returned unchanged, since it needs
 * no anchoring — callers that only ever hold a calendar day (the event-member
 * PATCH route enforces that with a Zod regex) will never hit that branch.
 */
export function dateOnlyToSalesforceDatetime(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return DATE_ONLY.test(value) ? `${value}T12:00:00.000Z` : value;
}

/** Whole days from local-midnight-today to the given date-only string (event date). */
export function daysUntil(value: string | null | undefined): number | null {
  const target = parseDateOnly(value);
  if (!target) return null;
  const today = parseDateOnly(todayIso());
  if (!today) return null;
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}
